import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Layout from './components/layout/Layout';
import { PermissionGuard } from './components/auth/PermissionGuard';
import { CompanyPremiumGuard } from './components/auth/CompanyPremiumGuard';
import PageLoading from './components/ui/PageLoading';
import NativeAppOnboarding from './components/native/NativeAppOnboarding';
import CraftsmanSetupPage from './pages/CraftsmanSetupPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import { Toaster } from 'react-hot-toast';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { useAppContext } from './context/AppContextCompat';
import { getEmployeeRoute } from './utils/employeeRoutes';
import ConsentChecker from './components/legal/ConsentChecker';
import { registerAppNavigate } from './utils/appNavigate';

// 懶加載頁面組件
const HomePage = React.lazy(() => import('./pages/HomePage'));
const AuthPage = React.lazy(() => import('./pages/AuthPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const MobileMorePage = React.lazy(() => import('./pages/MobileMorePage'));
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const CraftsmanSearchPage = React.lazy(() => import('./pages/CraftsmanSearchPage'));
const ServiceRequestPage = React.lazy(() => import('./pages/ServiceRequestPage'));
const PaymentConfirmationPage = React.lazy(() => import('./pages/PaymentConfirmationPage'));
const ServiceHistoryPage = React.lazy(() => import('./pages/ServiceHistoryPage'));
const CustomerDashboardPage = React.lazy(() => import('./pages/CustomerDashboardPage'));
const CraftsmanDashboardPage = React.lazy(() => import('./pages/CraftsmanDashboardPage'));
const CraftsmanProfilePage = React.lazy(() => import('./pages/CraftsmanProfilePage'));
const CraftsmanReviewsPage = React.lazy(() => import('./pages/CraftsmanReviewsPage'));
const CraftsmanJobsPage = React.lazy(() => import('./pages/CraftsmanJobsPage'));
const CraftsmanEarningsPage = React.lazy(() => import('./pages/CraftsmanEarningsPage'));
const CompanyDashboardPage = React.lazy(() => import('./pages/CompanyDashboardPage'));
const CompanyProfilePage = React.lazy(() => import('./pages/CompanyProfilePage'));
const CompanyReviewsPage = React.lazy(() => import('./pages/CompanyReviewsPage'));
const CompanyPremiumPage = React.lazy(() => import('./pages/CompanyPremiumPage'));
const CompanyJobsPage = React.lazy(() => import('./pages/CompanyJobsPage'));
const CompanyEarningsPage = React.lazy(() => import('./pages/CompanyEarningsPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCompletePage = React.lazy(() => import('./pages/PaymentCompletePage'));
const PaymentFailedPage = React.lazy(() => import('./pages/PaymentFailedPage'));
const CompanyEmployeesPage = React.lazy(() => import('./pages/CompanyEmployeesPage'));
const CraftsmenPage = React.lazy(() => import('./pages/CraftsmenPage'));
const CraftsmanDetailPage = React.lazy(() => import('./pages/CraftsmanDetailPage'));
const CompanyDetailPage = React.lazy(() => import('./pages/CompanyDetailPage'));
const CommissionStatsPage = React.lazy(() => import('./pages/CommissionStatsPage'));
const MarketAnalyticsPage = React.lazy(() => import('./pages/MarketAnalyticsPage'));
const ABTestManagementPage = React.lazy(() => import('./pages/admin/ABTestManagementPage'));
const ServiceCompletionPage = React.lazy(() => import('./pages/ServiceCompletionPage'));
const ServiceTrackingPage = React.lazy(() => import('./pages/ServiceTrackingPage'));
const CraftsmanNotificationsPage = React.lazy(() => import('./pages/CraftsmanNotificationsPage'));
const CustomerNotificationsPage = React.lazy(() => import('./pages/CustomerNotificationsPage'));
const MessageSystemTestPage = React.lazy(() => import('./pages/MessageSystemTestPage'));
const SentryTestPage = React.lazy(() => import('./pages/SentryTestPage'));
const MessagesPage = React.lazy(() => import('./pages/MessagesPage'));
const EmployeeDashboardPage = React.lazy(() => import('./pages/EmployeeDashboardPage'));
const EmployeeJobsPage = React.lazy(() => import('./pages/EmployeeJobsPage'));
const EmployeeEarningsPage = React.lazy(() => import('./pages/EmployeeEarningsPage'));
const EmployeePaymentsPage = React.lazy(() => import('./pages/EmployeePaymentsPage'));
const EmployeeInvoicesPage = React.lazy(() => import('./pages/EmployeeInvoicesPage'));
const EmployeeDocumentsPage = React.lazy(() => import('./pages/EmployeeDocumentsPage'));
const EmployeeAdministrativePage = React.lazy(() => import('./pages/EmployeeAdministrativePage'));
const FinancialReportsPage = React.lazy(() => import('./pages/FinancialReportsPage'));
const ExpenseManagementPage = React.lazy(() => import('./pages/ExpenseManagementPage'));
const CashFlowManagementPage = React.lazy(() => import('./pages/CashFlowManagementPage'));
const ReceivablesPayablesPage = React.lazy(() => import('./pages/ReceivablesPayablesPage'));
const CostAnalysisPage = React.lazy(() => import('./pages/CostAnalysisPage'));
const BudgetManagementPage = React.lazy(() => import('./pages/BudgetManagementPage'));
const RiskMonitoringPage = React.lazy(() => import('./pages/RiskMonitoringPage'));
const BlacklistManagementPage = React.lazy(() => import('./pages/BlacklistManagementPage'));
const TechnicianManagementPage = React.lazy(() => import('./pages/TechnicianManagementPage'));
const DailyReconciliationPage = React.lazy(() => import('./pages/DailyReconciliationPage'));
const MonthlySettlementPage = React.lazy(() => import('./pages/MonthlySettlementPage'));
const AdminDashboardPage = React.lazy(() => import('./pages/AdminDashboardPage'));
const AdminTechnicianReviewPage = React.lazy(
  () => import('./pages/AdminTechnicianReviewPage')
);
const AdminDisputesPage = React.lazy(() => import('./pages/AdminDisputesPage'));
const AdminCommissionConfigPage = React.lazy(
  () => import('./pages/AdminCommissionConfigPage')
);
const AdminReviewReportsPage = React.lazy(() => import('./pages/AdminReviewReportsPage'));
const AdminReviewsPage = React.lazy(() => import('./pages/AdminReviewsPage'));
const AdminIdentityVerificationsPage = React.lazy(
  () => import('./pages/AdminIdentityVerificationsPage')
);
const AdminCompanyBusinessVerificationPage = React.lazy(
  () => import('./pages/AdminCompanyBusinessVerificationPage')
);
const AdminPremiumMonitoringPage = React.lazy(() => import('./pages/AdminPremiumMonitoringPage'));
const AdminImprovementPlansPage = React.lazy(() => import('./pages/AdminImprovementPlansPage'));
const AdminRatingMonitorPage = React.lazy(() => import('./pages/AdminRatingMonitorPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const UserAgreementPage = React.lazy(() => import('./pages/UserAgreementPage'));
const RefundPolicyPage = React.lazy(() => import('./pages/RefundPolicyPage'));
const LegalDocumentsPage = React.lazy(() => import('./pages/LegalDocumentsPage'));
const DataProtectionPage = React.lazy(() => import('./pages/DataProtectionPage'));
const BusinessRegistrationPage = React.lazy(() => import('./pages/BusinessRegistrationPage'));
const TaxHandlingPage = React.lazy(() => import('./pages/TaxHandlingPage'));

/** 信箱確認信若導向首頁（Site URL=/），將 hash/query 帶到 /auth 由 AuthPage 處理 */
const AppNavigateBridge: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    registerAppNavigate((path) => navigate(path));
    return () => registerAppNavigate(null);
  }, [navigate]);
  return null;
};

const EmailConfirmToAuthRedirect: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== '/') return;
    const h = location.hash || '';
    const qs = location.search || '';
    const code = new URLSearchParams(qs).get('code');
    const isAuthEmailCallback =
      h.includes('access_token') ||
      h.includes('type=signup') ||
      h.includes('type=recovery') ||
      !!code;
    if (!isAuthEmailCallback) return;
    navigate(`/auth${qs}${h}`, { replace: true });
  }, [location.pathname, location.hash, location.search, navigate]);

  return null;
};

const EmployeeDashboardEntry: React.FC = () => {
  const { currentUser, isLoading } = useAppContext();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  const targetRoute = getEmployeeRoute('dashboard', currentUser.userType);
  if (targetRoute !== '/employee/dashboard') {
    return <Navigate to={targetRoute} replace />;
  }

  return (
    <PermissionGuard requiredUserType={['administrative_staff', 'finance_staff', 'other_staff']}>
      <EmployeeDashboardPage />
    </PermissionGuard>
  );
};

const EmployeeJobsEntry: React.FC = () => {
  const { currentUser, isLoading } = useAppContext();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  const targetRoute = getEmployeeRoute('jobs', currentUser.userType);
  if (targetRoute !== '/employee/jobs') {
    return <Navigate to={targetRoute} replace />;
  }

  return (
    <PermissionGuard requiredUserType={['administrative_staff', 'finance_staff', 'other_staff']}>
      <EmployeeJobsPage />
    </PermissionGuard>
  );
};

const EmployeeProfileEntry: React.FC = () => {
  const { currentUser, isLoading } = useAppContext();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  const targetRoute = getEmployeeRoute('profile', currentUser.userType);
  if (targetRoute !== '/employee/profile') {
    return <Navigate to={targetRoute} replace />;
  }

  return (
    <PermissionGuard requiredUserType={['administrative_staff', 'finance_staff', 'other_staff']}>
      <ProfilePage />
    </PermissionGuard>
  );
};

const EmployeeDocumentsEntry: React.FC = () => {
  const { currentUser, isLoading } = useAppContext();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  const targetRoute = getEmployeeRoute('documents', currentUser.userType);
  if (targetRoute !== '/employee/documents') {
    return <Navigate to={targetRoute} replace />;
  }

  return (
    <PermissionGuard requiredUserType={['employee', 'administrative_staff', 'other_staff']}>
      <EmployeeDocumentsPage />
    </PermissionGuard>
  );
};

const App: React.FC = () => {
  return (
    <>
      <AppNavigateBridge />
      <EmailConfirmToAuthRedirect />
      <NativeAppOnboarding />
      {/* 法規文件更新提醒（黃色提示板）已停用 */}
      <Suspense fallback={<PageLoading />}>
        <Routes>
                <Route path="/" element={<Layout />}>
                  {/* 公開頁面 */}
                  <Route index element={<HomePage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/more" element={<MobileMorePage />} />
                  <Route path="/landing" element={<Navigate to="/about" replace />} />
                  <Route path="/auth" element={<AuthPage />} />
                  
                  {/* 法律文檔頁面 */}
                  <Route path="/legal" element={<LegalDocumentsPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/user-agreement" element={<UserAgreementPage />} />
                  <Route path="/refund-policy" element={<RefundPolicyPage />} />
                  <Route path="/data-protection" element={<DataProtectionPage />} />
                  <Route path="/business-registration" element={<BusinessRegistrationPage />} />
                  <Route path="/tax-handling" element={<TaxHandlingPage />} />
                  
                  {/* 搜索页面 - 公开访问 */}
                  <Route path="/search" element={<CraftsmanSearchPage />} />
                  
                  {/* 添加工匠搜索页面路由 */}
                  <Route path="/craftsman-search" element={<CraftsmanSearchPage />} />

                  {/* 添加 /request 路由作为 /service-request 的别名 */}
                  <Route path="/request" element={<ServiceRequestPage />} />

                  {/* 客戶專用頁面 */}
                  <Route
                    path="/service-request"
                    element={
                      <PermissionGuard requiredUserType="customer">
                        <ServiceRequestPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/payment-confirmation"
                    element={
                      <PermissionGuard requiredUserType="customer">
                        <PaymentConfirmationPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/payment/complete"
                    element={<PaymentCompletePage />}
                  />
                  <Route path="/payment-failed" element={<PaymentFailedPage />} />
                  <Route
                    path="/payment/cancel"
                    element={<PaymentFailedPage />}
                  />
                  <Route
                    path="/payment-success"
                    element={
                      <PermissionGuard requiredUserType="customer">
                        <PaymentSuccessPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/service-history"
                    element={
                      <PermissionGuard requiredUserType="customer">
                        <ServiceHistoryPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/customer/dashboard"
                    element={
                      <PermissionGuard requiredUserType="customer">
                        <CustomerDashboardPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/customer/notifications"
                    element={<CustomerNotificationsPage />}
                  />
                  <Route
                    path="/customer/notifications-protected"
                    element={
                      <PermissionGuard requiredUserType="customer">
                        <CustomerNotificationsPage />
                      </PermissionGuard>
                    }
                  />
                  {/* 添加 /customer-dashboard 作为别名，重定向到 /customer/dashboard */}
                  <Route
                    path="/customer-dashboard"
                    element={<Navigate to="/customer/dashboard" replace />}
                  />

                  {/* 個人工匠專用頁面 */}
                  <Route
                    path="/craftsman/dashboard"
                    element={
                      <PermissionGuard requiredUserType="craftsman">
                        <CraftsmanDashboardPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/craftsman/setup"
                    element={
                      <PermissionGuard requiredUserType="craftsman">
                        <CraftsmanSetupPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/craftsman/profile"
                    element={
                      <PermissionGuard requiredUserType="craftsman">
                        <CraftsmanProfilePage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/craftsman/reviews"
                    element={
                      <PermissionGuard requiredUserType="craftsman">
                        <CraftsmanReviewsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/craftsman/jobs"
                    element={
                      <PermissionGuard requiredUserType="craftsman">
                        <CraftsmanJobsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/craftsman/earnings"
                    element={
                      <PermissionGuard requiredUserType="craftsman">
                        <CraftsmanEarningsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/craftsman/notifications"
                    element={
                      <PermissionGuard requiredUserType="craftsman">
                        <CraftsmanNotificationsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/service-completion/:requestId"
                    element={
                      <PermissionGuard requiredUserType="craftsman">
                        <ServiceCompletionPage />
                      </PermissionGuard>
                    }
                  />

                  {/* 公司專用頁面 */}
                  <Route
                    path="/company/dashboard"
                    element={
                      <PermissionGuard requiredUserType="company">
                        <CompanyPremiumGuard>
                          <CompanyDashboardPage />
                        </CompanyPremiumGuard>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/company/profile"
                    element={
                      <PermissionGuard requiredUserType="company">
                        <CompanyProfilePage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/company/premium"
                    element={
                      <PermissionGuard requiredUserType="company">
                        <CompanyPremiumPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/company/jobs"
                    element={
                      <PermissionGuard requiredUserType={['company', 'administrative_staff']}>
                        <CompanyPremiumGuard>
                          <CompanyJobsPage />
                        </CompanyPremiumGuard>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/company/earnings"
                    element={
                      <PermissionGuard requiredUserType={['company', 'finance_staff']}>
                        <CompanyPremiumGuard>
                          <CompanyEarningsPage />
                        </CompanyPremiumGuard>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/company/employees"
                    element={
                      <PermissionGuard requiredUserType={['company', 'administrative_staff', 'finance_staff']}>
                        <CompanyPremiumGuard>
                          <CompanyEmployeesPage />
                        </CompanyPremiumGuard>
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/company/reviews"
                    element={
                      <PermissionGuard requiredUserType="company">
                        <CompanyPremiumGuard>
                          <CompanyReviewsPage />
                        </CompanyPremiumGuard>
                      </PermissionGuard>
                    }
                  />

                  {/* 員工專用頁面 */}
                  <Route path="/employee/dashboard" element={<EmployeeDashboardEntry />} />
                  <Route
                    path="/employee/dashboard/customer-service"
                    element={
                      <PermissionGuard requiredUserType="customer_service">
                        <EmployeeDashboardPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/dashboard/maintenance"
                    element={
                      <PermissionGuard requiredUserType="employee">
                        <EmployeeDashboardPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/dashboard/administrative"
                    element={
                      <PermissionGuard requiredUserType="administrative_staff">
                        <EmployeeDashboardPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/dashboard/finance"
                    element={
                      <PermissionGuard requiredUserType="finance_staff">
                        <EmployeeDashboardPage />
                      </PermissionGuard>
                    }
                  />
                  <Route path="/employee/jobs" element={<EmployeeJobsEntry />} />
                  <Route
                    path="/employee/jobs/customer-service"
                    element={
                      <PermissionGuard requiredUserType="customer_service">
                        <EmployeeJobsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/jobs/maintenance"
                    element={
                      <PermissionGuard requiredUserType="employee">
                        <EmployeeJobsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/jobs/administrative"
                    element={
                      <PermissionGuard requiredUserType="administrative_staff">
                        <EmployeeJobsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/jobs/finance"
                    element={
                      <PermissionGuard requiredUserType="finance_staff">
                        <EmployeeJobsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route path="/employee/profile" element={<EmployeeProfileEntry />} />
                  <Route
                    path="/employee/profile/customer-service"
                    element={
                      <PermissionGuard requiredUserType="customer_service">
                        <ProfilePage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/profile/maintenance"
                    element={
                      <PermissionGuard requiredUserType="employee">
                        <ProfilePage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/profile/administrative"
                    element={
                      <PermissionGuard requiredUserType="administrative_staff">
                        <ProfilePage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/profile/finance"
                    element={
                      <PermissionGuard requiredUserType="finance_staff">
                        <ProfilePage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/earnings"
                    element={
                      <PermissionGuard requiredUserType={['employee', 'finance_staff', 'other_staff']}>
                        <EmployeeEarningsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/payments"
                    element={
                      <PermissionGuard requiredUserType={['employee', 'finance_staff', 'other_staff']}>
                        <EmployeePaymentsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/invoices"
                    element={
                      <PermissionGuard requiredUserType={['employee', 'finance_staff', 'other_staff']}>
                        <EmployeeInvoicesPage />
                      </PermissionGuard>
                    }
                  />
                  <Route path="/employee/documents" element={<EmployeeDocumentsEntry />} />
                  <Route
                    path="/employee/documents/administrative"
                    element={
                      <PermissionGuard requiredUserType="administrative_staff">
                        <EmployeeDocumentsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/administrative"
                    element={
                      <PermissionGuard requiredUserType={['employee', 'administrative_staff', 'other_staff']}>
                        <EmployeeAdministrativePage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/financial-reports"
                    element={
                      <PermissionGuard requiredUserType={['employee', 'finance_staff', 'other_staff']}>
                        <FinancialReportsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/expense-management"
                    element={
                      <PermissionGuard requiredUserType="finance_staff">
                        <ExpenseManagementPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/cashflow-management"
                    element={
                      <PermissionGuard requiredUserType="finance_staff">
                        <CashFlowManagementPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/daily-reconciliation"
                    element={
                      <PermissionGuard requiredUserType="finance_staff">
                        <DailyReconciliationPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/monthly-settlement"
                    element={
                      <PermissionGuard requiredUserType="finance_staff">
                        <MonthlySettlementPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/receivables-payables"
                    element={
                      <PermissionGuard requiredUserType={['employee', 'finance_staff', 'other_staff']}>
                        <ReceivablesPayablesPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/cost-analysis"
                    element={
                      <PermissionGuard requiredUserType={['employee', 'finance_staff', 'other_staff']}>
                        <CostAnalysisPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/budget-management"
                    element={
                      <PermissionGuard requiredUserType={['employee', 'finance_staff', 'other_staff']}>
                        <BudgetManagementPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/technicians"
                    element={
                      <PermissionGuard requiredUserType={['administrative_staff', 'other_staff']}>
                        <TechnicianManagementPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/employee/dashboard/administrative/technicians"
                    element={
                      <PermissionGuard requiredUserType={['administrative_staff', 'other_staff']}>
                        <TechnicianManagementPage />
                      </PermissionGuard>
                    }
                  />

                  {/* 通用页面 */}
                  <Route
                    path="/profile"
                    element={
                      <PermissionGuard requiredUserType={['customer', 'craftsman', 'company', 'admin', 'employee', 'customer_service', 'administrative_staff', 'finance_staff', 'other_staff']}>
                        <ProfilePage />
                      </PermissionGuard>
                    }
                  />

                  {/* 添加工匠列表页面 */}
                  <Route path="/craftsmen" element={<CraftsmenPage />} />
                  <Route path="/craftsman/:id" element={<CraftsmanDetailPage />} />
                  
                  {/* 公司詳情頁面 - 公開訪問 */}
                  <Route path="/company/:id" element={<CompanyDetailPage />} />

                  {/* 消息系統測試頁面 - 需要登入 */}
                  <Route
                    path="/message-system-test"
                    element={
                      <PermissionGuard requiredUserType={['customer', 'craftsman', 'company', 'admin']}>
                        <MessageSystemTestPage />
                      </PermissionGuard>
                    }
                  />

                  {/* Sentry 測試頁面 - 管理員專用 */}
                  <Route
                    path="/sentry-test"
                    element={
                      <PermissionGuard requiredUserType={['admin']}>
                        <SentryTestPage />
                      </PermissionGuard>
                    }
                  />

                  {/* 消息頁面 - 所有用戶類型都可訪問 */}
                  <Route path="/message" element={<Navigate to="/messages" replace />} />
                  <Route
                    path="/messages"
                    element={
                      <PermissionGuard requiredUserType={['customer', 'craftsman', 'company', 'admin', 'employee', 'customer_service', 'administrative_staff', 'finance_staff', 'other_staff']}>
                        <MessagesPage />
                      </PermissionGuard>
                    }
                  />

                  {/* 佣金统计页面 - 僅管理員可訪問 */}
                  <Route
                    path="/commission-stats"
                    element={
                      <PermissionGuard requiredUserType={['admin']}>
                        <CommissionStatsPage />
                      </PermissionGuard>
                    }
                  />

                  {/* 市场分析页面 - 所有用户可访问 */}
                  <Route
                    path="/market-analytics"
                    element={
                      <PermissionGuard requiredUserType={['customer', 'craftsman', 'company', 'admin', 'employee']}>
                        <MarketAnalyticsPage />
                      </PermissionGuard>
                    }
                  />

                  {/* Admin 專用頁面 */}
                  <Route
                    path="/admin/dashboard"
                    element={
                      <PermissionGuard requiredUserType="admin">
                        <AdminDashboardPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/admin/ab-tests"
                    element={
                      <PermissionGuard requiredUserType="admin">
                        <ABTestManagementPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/admin/technicians"
                    element={
                      <PermissionGuard requiredUserType="admin">
                        <AdminTechnicianReviewPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/admin/disputes"
                    element={
                      <PermissionGuard requiredUserType="admin">
                        <AdminDisputesPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/admin/commission"
                    element={
                      <PermissionGuard requiredUserType="admin">
                        <AdminCommissionConfigPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/admin/reviews"
                    element={
                      <PermissionGuard requiredUserType={['admin', 'administrative_staff']}>
                        <AdminReviewsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/admin/review-reports"
                    element={
                      <PermissionGuard requiredUserType={['admin', 'administrative_staff']}>
                        <AdminReviewReportsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/admin/identity-verifications"
                    element={
                      <PermissionGuard requiredUserType="admin">
                        <AdminIdentityVerificationsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/admin/company-business-verification"
                    element={
                      <PermissionGuard requiredUserType="admin">
                        <AdminCompanyBusinessVerificationPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/admin/premium-monitoring"
                    element={
                      <PermissionGuard requiredUserType="admin">
                        <AdminPremiumMonitoringPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/admin/improvement-plans"
                    element={
                      <PermissionGuard requiredUserType="admin">
                        <AdminImprovementPlansPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/admin/rating-monitor"
                    element={
                      <PermissionGuard requiredUserType="admin">
                        <AdminRatingMonitorPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/risk-monitoring"
                    element={
                      <PermissionGuard requiredUserType={['admin', 'customer_service', 'finance_staff', 'administrative_staff', 'employee']}>
                        <RiskMonitoringPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="/admin/blacklist"
                    element={
                      <PermissionGuard requiredUserType={['admin', 'customer_service', 'finance_staff', 'administrative_staff']}>
                        <BlacklistManagementPage />
                      </PermissionGuard>
                    }
                  />

                  {/* 服務追蹤頁面 - 客戶和工匠都可訪問 */}
                  <Route
                    path="/tracking/:orderId"
                    element={
                      <PermissionGuard requiredUserType={['customer', 'craftsman', 'company']}>
                        <ServiceTrackingPage />
                      </PermissionGuard>
                    }
                  />

                  {/* 404 页面 */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                </Route>
              </Routes>
            </Suspense>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#333',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          error: {
            duration: 12000,
            style: {
              background: '#fee',
              color: '#c33',
            },
          },
        }}
      />
    </>
  );
};

export default App;